import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { FileUploader } from "@/shared/components";
import { Input, SearchableSelect, Toast } from "@/shared/ui";
import { useDocumentSuggest } from "@/shared/hooks";
import { applyOcrFields } from "@/shared/utils";
import { BANKS, type Bank } from "@/shared/consts/banks";
import { editBankingSchema, type EditBankingValues } from "./validation";
import type { SectionFormHandle } from "./types";

interface BankingSection {
  bank_name: string;
  iban_account: string;
  bik_number: string;
  bank_certificate_file_url: string | null;
}

interface Props {
  banking: BankingSection | null;
  draftValues?: BankingSection | null;
}

function mapBankingToEditValues(banking: BankingSection | null): EditBankingValues {
  const bank = BANKS.find((b) => b.name === (banking?.bank_name ?? "")) ?? null;
  return {
    bank_name: banking?.bank_name ?? "",
    iban_account: banking?.iban_account ?? "",
    bik_number: bank?.bik ?? banking?.bik_number ?? "",
  };
}

const EditBankingSection = forwardRef<SectionFormHandle, Props>(function EditBankingSection({ banking, draftValues }, ref) {
  const { t } = useTranslation("EmployeePortal");

  const initialValues = useRef<EditBankingValues>(mapBankingToEditValues(banking));
  const displayBanking = draftValues ?? banking;

  const [certFile, setCertFile] = useState<File | null>(null);
  const [existingCertRemoved, setExistingCertRemoved] = useState(false);
  const [ocrFilled, setOcrFilled] = useState<Set<string>>(new Set());
  const [ocrToast, setOcrToast] = useState(false);
  const [ocrWarning, setOcrWarning] = useState(false);
  const { scan: scanDocument, state: ocrState, reset: resetOcr } = useDocumentSuggest();

  const {
    register,
    watch,
    setValue,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<EditBankingValues>({
    resolver: zodResolver(editBankingSchema),
    mode: "onTouched",
    defaultValues: mapBankingToEditValues(displayBanking),
  });

  const currentBankName = watch("bank_name");
  const currentBank = BANKS.find((b) => b.name === currentBankName) ?? null;

  function handleBankChange(bank: Bank | null) {
    setValue("bank_name", bank?.name ?? "", { shouldValidate: true });
    setValue("bik_number", bank?.bik ?? "", { shouldValidate: true });
  }

  function resolveBankFromOcr(fields: Record<string, string>): Bank | null {
    const byBik = fields.bik_number ? BANKS.find((b) => b.bik === fields.bik_number) : null;
    if (byBik) return byBik;
    const byName = fields.bank_name ? BANKS.find((b) => b.name === fields.bank_name) : null;
    return byName ?? null;
  }

  async function runBankOcr(file: File) {
    setOcrToast(false);
    setOcrWarning(false);
    const result = await scanDocument(file, "bank_certificate");
    if (result.status === "idle") return;
    const fields = result.fields ?? {};

    const matched =
      (fields.iban_account?.trim() ? 1 : 0) + (fields.bank_name?.trim() || fields.bik_number?.trim() ? 1 : 0);
    if (matched * 2 < 2) {
      setOcrWarning(true);
      return;
    }

    const filled: string[] = [];

    const ibanResult = applyOcrFields({ iban_account: getValues("iban_account") ?? "" }, fields, ["iban_account"]);
    if (ibanResult.filled.length > 0) {
      setValue("iban_account", ibanResult.next.iban_account, { shouldValidate: true });
      filled.push("iban_account");
    }

    if ((getValues("bank_name") ?? "").trim() === "") {
      const matchedBank = resolveBankFromOcr(fields);
      if (matchedBank) {
        handleBankChange(matchedBank);
        filled.push("bank_name");
      }
    }

    if (filled.length > 0) {
      setOcrFilled(new Set(filled));
      setOcrToast(true);
    }
  }

  function handleCertChange(file: File | null) {
    setCertFile(file);
    if (!file) {
      resetOcr();
      setOcrFilled(new Set());
      setOcrToast(false);
      setOcrWarning(false);
      return;
    }
    void runBankOcr(file);
  }

  useImperativeHandle(ref, function buildHandle() {
    return {
      getChanges() {
        const current = getValues();
        const initial = initialValues.current;
        const changes: Record<string, unknown> = {};

        if (current.bank_name !== initial.bank_name) changes.bank_name = current.bank_name;
        if (current.iban_account !== initial.iban_account) changes.iban_account = current.iban_account;
        if (current.bik_number !== initial.bik_number) changes.bik_number = current.bik_number;

        return Object.keys(changes).length > 0 ? changes : null;
      },
      getFiles() {
        if (certFile) return { "banking.bank_certificate_file": certFile };
        return null;
      },
      async validate() {
        return await trigger();
      },
      isDirty() {
        const current = getValues();
        const initial = initialValues.current;
        return (
          current.bank_name !== initial.bank_name ||
          current.iban_account !== initial.iban_account ||
          current.bik_number !== initial.bik_number ||
          certFile !== null
        );
      },
    };
  });

  return (
    <div className="flex flex-col gap-4">
      {ocrState === "scanning" && (
        <Toast color="grey" text={t("personalSheet.ocr.scanning")} closable={false} autoClose={false} isFullWidth />
      )}
      {ocrToast && (
        <Toast
          key="bank-ocr-filled"
          color="positive"
          text={t("personalSheet.ocr.filled")}
          isFullWidth
          onClose={() => setOcrToast(false)}
        />
      )}
      {ocrWarning && (
        <Toast
          key="bank-ocr-warning"
          color="notice"
          text={t("personalSheet.ocr.wrongFile.bank_certificate")}
          isFullWidth
          onClose={() => setOcrWarning(false)}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SearchableSelect<Bank>
          label={t("personalSheet.fields.bankName")}
          hasRequiredLabel
          placeholder={t("personalSheet.fields.bankName")}
          options={BANKS}
          value={currentBank}
          onChange={handleBankChange}
          searchKeys={["name"]}
          displayKey="name"
          highlight={ocrFilled.has("bank_name")}
          error={errors.bank_name?.message ? t(errors.bank_name.message) : undefined}
        />

        <Input
          label={t("personalSheet.fields.ibanAccount")}
          hasRequiredLabel
          placeholder={t("personalSheet.fields.ibanAccount")}
          highlight={ocrFilled.has("iban_account")}
          {...register("iban_account")}
          error={errors.iban_account?.message ? t(errors.iban_account.message) : undefined}
        />
      </div>

      <FileUploader
        label={t("personalSheet.fields.bankCertificateFile")}
        hasRequiredLabel
        value={certFile}
        existingFileUrl={existingCertRemoved ? null : (displayBanking?.bank_certificate_file_url ?? null)}
        onChange={handleCertChange}
        onRemoveExisting={() => setExistingCertRemoved(true)}
        accept=".pdf,.docx,.jpg,.jpeg,.png"
      />
    </div>
  );
});

export default EditBankingSection;
